<template>
    <div>
        <el-row>
            <el-col v-if="currentTask"
                    :span="24">
                <el-row>
                    <el-col :span="dependent ? 24 : 12">
                        <el-form ref="taskTypeAcceptanceOfWork"
                                 v-loading="loading"
                                 :model="editedItem"
                                 label-width="140px"
                                 :label-position="dependent ? 'top' : 'left'"
                                 size="mini"
                                 @submit.native.prevent>
                            <!-- Name -->
                            <el-form-item label="Название задачи"
                                          prop="name"
                                          :error="errors && errors.name ? errors.name[0] : ''">
                                <el-input v-model="editedItem.name"
                                          disabled
                                          placeholder="Введите название задачи" />
                            </el-form-item>

                            <!-- Created at -->
                            <date-field v-model="editedItem.created_at"
                                        label="Дата создания"
                                        disabled />

                            <!-- Start at -->
                            <date-field v-model="editedItem.start_at"
                                        label="Дата приемки"
                                        disabled />

                            <!-- Time (by worker schedule) -->
                            <input-field v-if="hasBookedSchedule(task)"
                                         label="Время приемки"
                                         :value="bookedScheduleLabel"
                                         disabled />

                            <!-- Project -->
                            <input-field v-model="editedItem.project_name"
                                         label="Проект"
                                         disabled />

                            <!-- Creator -->
                            <user-field v-model="editedItem.initiator_id"
                                        label="Инициатор"
                                        as-key
                                        disabled
                                        with-blocked />

                            <!-- Contractor -->
                            <user-field v-model="editedItem.contractor_id"
                                        label="Подрядчик"
                                        :disabled="!isEditableField('new_contractor_id')"
                                        :errors="errors['new_contractor_id']"
                                        is-contractor
                                        as-key
                                        with-blocked>
                                <template #extra-fields>
                                    <!-- CC history -->
                                    <trashed-members v-if="trashedMembers.contractors?.length"
                                                     :members="trashedMembers.contractors"
                                                     placement="right-start" />
                                </template>
                            </user-field>

                            <!-- Worker (first stage) -->
                            <user-field v-model="editedItem.worker_id"
                                        label="Инженер стройконтроля"
                                        :disabled="!isEditableField('new_engineer_first_stage_id')"
                                        :errors="errors['new_engineer_first_stage_id']"
                                        is-engineer
                                        as-key
                                        with-blocked>
                                <template #extra-fields>
                                    <!-- Worker (first stage) history -->
                                    <trashed-members v-if="trashedMembers.engineers?.length"
                                                     :members="trashedMembers.engineers"
                                                     placement="right-start" />
                                </template>
                            </user-field>
                            
                            <!-- Worker (second stage) -->
                            <user-field v-if="isEditableField('new_engineer_second_stage_id')"
                                        v-model="editedItem.new_engineer_second_stage_id"
                                        label="Инженер СКЗ"
                                        :errors="errors['new_engineer_second_stage_id']"
                                        is-general-engineer
                                        as-key
                                        with-blocked>
                                <template #extra-fields>
                                    <!-- Worker (second stage) history -->
                                    <trashed-members v-if="trashedMembers.general_engineers?.length"
                                                     :members="trashedMembers.general_engineers"
                                                     placement="right-start" />
                                </template>
                            </user-field>

                            <!-- Jobs -->
                            <el-form-item label="Выбор ГПР"
                                          prop="job_types"
                                          :error="errors && errors.job_types ? errors.job_types[0] : ''">
                                <el-cascader v-model="editedItem.job_types"
                                             :options="jobTypesTree"
                                             :props="{
                                                 multiple: true,
                                                 label: 'name',
                                                 value: 'id',
                                                 checkStrictly: true,
                                                 emitPath: false
                                             }"
                                             disabled
                                             style="width: 100%"
                                             clearable />
                            </el-form-item>

                            <!-- Contract -->
                            <contract-field v-if="editedItem.ast_contract"
                                            v-model="contract"
                                            :ast-contract="editedItem.ast_contract"
                                            :task-id="id"
                                            :work-errors="errors['ast_works']"

                                            :disabled="!canEditContract"
                                            :ast-contract-disabled="!isEditableField('ast_contract_id')"
                                            :ast-work-facts-disabled="!isEditableField('ast_work_facts')"

                                            :editable-work-facts-field="editableWorkFactsField"
                                            
                                            with-works
                                            with-works-selection
                                            :with-work-checkbox="isEditableField('ast_work_ids')"
                                            
                                            :with-ks2-enabled="editedItem.ast_act_ks2_enabled !== null"
                                            :ks2-switcher-disabled="!isEditableField('ast_act_ks2_enabled')"
                                            :ks2-enabled="editedItem.ast_act_ks2_enabled"
                                            :with-ks2-errors="withKs2Errors"

                                            with-rejections-task-source
                                            
                                            @change-ks2-enabled="change('ast_act_ks2_enabled', $event)" />

                            <!-- Initiator comment -->
                            <input-field v-model="editedItem.comment_initiator"
                                         label="Принимаемые работы"
                                         name="comment_initiator"
                                         type="textarea"
                                         :disabled="!isEditableField('comment_initiator')"
                                         :errors="errors['comment_initiator']" />

                            <!-- Axis -->
                            <input-field v-model="editedItem.axis"
                                         label="В осях"
                                         name="axis"
                                         :required="isEditableField('axis') && isRequiredField('axis')"
                                         :disabled="!isEditableField('axis')"
                                         :errors="errors['axis']" />

                            <!-- PWP -->
                            <file-field v-if="canEditPWP"
                                        v-model="documents"
                                        label="ППР"
                                        with-document
                                        :modal="!dependent"
                                        :with-document-options="{
                                            forAcceptance: true
                                        }" />

                            <!-- Places (w) -->
                            <place-field v-if="isObjectEditing"
                                         v-model="editedItem.objects"
                                         :error="errors.objects"
                                         label="Список приемки"
                                         name="objects"
                                         multiple />

                            <!-- Places (r) -->
                            <el-form-item v-else
                                          label="Список приемки"
                                          prop="objects">
                                <div class="f-col">
                                    <span v-for="x in entities"
                                          :key="x.key"
                                          class="fs-0.75 text-gray-500">{{ x.label }}</span>
                                </div>
                                <div v-if="showAcceptanceEditButtons && isEditableField('objects')"
                                     class="fs-0.75 text-accent cursor-pointer"
                                     @click="isObjectEditing = true">
                                    <i class="el-icon-edit" /> Редактировать список приемки
                                </div>
                            </el-form-item>

                            <!-- Comment (E2) --> 
                            <el-form-item v-if="editedItem.comment_worker"
                                          label="Комментарий инженера СК Г/П"
                                          prop="comment_worker"
                                          :error="errors && errors.comment_worker ? errors.comment_worker[0] : ''">
                                <el-input v-model="editedItem.comment_worker"
                                          type="textarea"
                                          disabled
                                          :autosize="{minRows: 3}" />
                            </el-form-item>

                            <!-- Comment (E2) -->
                            <el-form-item v-if="editedItem.comment_worker_second_stage"
                                          label="Комментарий инженера СКЗ"
                                          prop="comment_worker"
                                          :error="errors && errors.comment_worker_second_stage ? errors.comment_worker_second_stage[0] : ''">
                                <el-input v-model="editedItem.comment_worker_second_stage"
                                          type="textarea"
                                          disabled
                                          :autosize="{minRows: 3}" />
                            </el-form-item>

                            <!-- Comment (CC) -->
                            <el-form-item v-if="editedItem.comment_contractor"
                                          label="Комментарий подрядчика"
                                          prop="comment_contractor"
                                          :error="errors && errors.comment_contractor ? errors.comment_contractor[0] : ''">
                                <el-input v-model="editedItem.comment_contractor"
                                          type="textarea"
                                          disabled
                                          :autosize="{minRows: 3}" />
                            </el-form-item>
                            <div v-if="currentTask.subjects_acceptance_of_work_connection && currentTask.subjects_acceptance_of_work_connection.length"
                                 class="f _mb-1.25">
                                <div class="f">
                                    <span class="w-8 _pr-0.75 text-sm text-gray-700">
                                        Связанные дефекты
                                    </span>
                                </div>
                                <div>
                                    <div v-for="(x, i) in currentTask.subjects_acceptance_of_work_connection"
                                         :key="i">
                                        <span :class="[x.task_subject.is_deleted ? 'text-decoration--line-through' : '']"
                                              class="text-accent fs-0.875  cursor-pointer hover:underline"
                                              @click="goToRelatedTask(x)">
                                            {{ getNested(x, 'task_subject.name', '') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Buttons -->
                            <div class="cols-minmax-min space-x-1">
                                <!-- Confirm -->
                                <el-button type="primary"
                                           :disabled="!editableFieldsRepresented"
                                           @click="handleUpdate">
                                    Сохранить
                                </el-button>

                                <!-- Actions (dependent) -->
                                <task-actions v-if="dependent"
                                              :task="task"
                                              :actions="actionsWithMood"
                                              :errors="errors"
                                              :editable-fields="editableFieldsWithValidation"
                                              as-dropdown
                                              @do-with-data="act" />
                            </div>
                        </el-form>
                    </el-col>
                </el-row>
            </el-col>
        </el-row>

        <!-- Actions (default) -->
        <portal v-if="!dependent"
                to="allowed-actions">
            <task-actions 
                :task="task"
                :actions="actionsWithMood"
                :errors="errors"
                :editable-fields="editableFieldsWithValidation"
                @do-with-data="act" />
        </portal>
    </div>
</template>
<script>
import {mapState, mapGetters, mapMutations, mapActions} from 'vuex';

import { formatForServer, parseAt } from '@/utils/date'
import { TASK_ACTIONS, TASK_MEMBER_SUB_ROLES } from '@/utils/tasks';
import { fromError } from '@/utils/common'
import { then } from '@/utils/immutable';

import {
  getCommentContractor, getCommentWorker, getCommentWorkerSecondStage,
  hasBookedSchedule, getBookedScheduleLabel, getEntities, 
  getAstWorks, getAstContract,
  getTrashedMembers,
  getContractorUser
} from '@/models/tasks'

import TaskActions from '@/components/tasks/TaskActions'
import DateField from '@/components/fields/DateField'
import InputField from '@/components/fields/InputField'
import FileField from '@/components/fields/FileField'
import UserField from '@/components/fields/UserField'
import PlaceField from '@/components/fields/PlaceField'
import ContractField from '@/components/fields/ContractField'
import TrashedMembers from '@/components/popovers/TrashedMembers'

export default {
  name: 'TaskTypeAcceptanceOfWork',
  components: {
    TaskActions,
    DateField,
    InputField,
    FileField,
    UserField,
    PlaceField,
    ContractField,
    TrashedMembers
  },
  props: {
    allowedActions: { type: Array, default: function () { return []; } },
    showAcceptanceEditButtons: { type: Boolean, default: false },
    dependent: { type: Boolean, default: false }
  },
  data() {
    return {
      initialItem: {},
      editedItem: {
        project_id: '',
        worker_id: '',
        objects: [],
        comment_initiator: '',
        project_name: '',
        initiator_id: '',
        contractor_id: '',
        axis: '',
        created_at: null,
        start_at: null,
        job_types: [],
        comment_contractor: '',
        comment_worker: '',
        comment_worker_second_stage: ''
      },
      id: '',
      status: '',
      expired: false,
      loading: false,
      errors: {},
      isObjectEditing: false,

      task: null,
      documents: [],
      contract: {},

      trashedMembers: {},

      hasBookedSchedule 
    }
  },
  computed: {
    ...mapState('tasks', {currentTask: state => state.task}),
    ...mapGetters('tasks', ['editableFieldsWithValidation', 'isEditableField', 'isRequiredField', 'isEditableFieldAny']),
    ...mapGetters('dirs', ['jobTypesTree']),

    editableFieldsRepresented() {
      return this.isEditableFieldAny(
        'comment_initiator',
        'axis',
        'objects',
        'file_work_production_id',
        'project_document_work_production_id',
        'ast_act_ks2_enabled',
        'ast_work_facts_acceptance_of_work_created',
        'ast_work_facts_acceptance_of_work_first_stage_finished',
        'ast_work_facts_acceptance_of_work_second_stage_finished',
        'new_contractor_id',
        'new_engineer_first_stage_id',
        'new_engineer_second_stage_id'
      )
    },

    canEditPWP() {
      return this.editableFieldsWithValidation.some(x => x.name === 'file_work_production_id')
    },

    actionsWithMood() {
      const to = ({ name, translated_name, warnings, messages, allowed_fields, allowed_fields_with_validation }) => {
        const [mood = 'primary', label = translated_name] = ({
          [TASK_ACTIONS.CANCEL]: ['danger'],
          [TASK_ACTIONS.TAKE_TO_WORK]: ['success', 'Согласовать время'],
          [TASK_ACTIONS.TAKE_TO_WORK_SECOND_STAGE]: ['success', 'Взять в работу'],
          [TASK_ACTIONS.FINISH]: ['success'],
          [TASK_ACTIONS.FINISH_SECOND_STAGE]: ['success'],
          [TASK_ACTIONS.FINISH_NOT_ACCEPT]: ['danger', 'Работы не приняты'],
          [TASK_ACTIONS.FINISH_NOT_ACCEPT_SECOND_STAGE]: ['danger', 'Работы не приняты'],
          [TASK_ACTIONS.REFUSE]: ['danger'],
          [TASK_ACTIONS.CHECK_PROJECT_WORK_PRODUCTION]: ['success'],
          [TASK_ACTIONS.NON_CHECK_PROJECT_WORK_PRODUCTION]: ['danger'],
          [TASK_ACTIONS.COMPLETE_REWORK]: ['success']
        })[name] || []

        return {
          name,
          translated_name: label,
          mood,
          warnings,
          messages,
          allowed_fields,
          allowed_fields_with_validation
        }
      }

      return this.allowedActions.map(to)
    },

    entities() {
      return getEntities(this.task)
    },

    bookedScheduleLabel() {
      return getBookedScheduleLabel(this.task)
    },

    canEditContract() {
      return this.isEditableField('ast_contract_id') ||
        this.isEditableField('ast_work_ids') || 
        this.isEditableField('ast_work_facts') ||
        this.isEditableField('ast_work_facts_acceptance_of_work_created') ||
        this.isEditableField('ast_work_facts_acceptance_of_work_first_stage_finished') ||
        this.isEditableField('ast_work_facts_acceptance_of_work_second_stage_finished')
    },

    editableWorkFactsField() {
      return [
        this.isEditableField('ast_work_facts_acceptance_of_work_created') && 'fact_in_units_acceptance_of_work_created',
        this.isEditableField('ast_work_facts_acceptance_of_work_first_stage_finished') && 'fact_in_units_acceptance_of_work_first_stage_finished',
        this.isEditableField('ast_work_facts_acceptance_of_work_second_stage_finished') && 'fact_in_units_acceptance_of_work_second_stage_finished'
      ].filter(is)
    },

    withKs2Errors() {
      return this.editedItem.ast_acts_ks2_errors_exists && this.editedItem.ast_act_ks2_enabled && !this.editedItem.ast_acts_ks2?.length
    }
  },
  watch: {
    currentTask: {
      deep: true,
      immediate: true,
      handler(val) {
        try {
          this.loading = true;
          this.initializeItem(val);
        } finally {
          this.loading = false;
        }
      }
    }
  },
  mounted() {
    this.getJobTypesTree({
      params: {
        filter: {project_id: this.$route.params.projectId}
      }
    });
  },
  methods: {
    ...mapMutations('dialog', {showDialog: 'SHOW_DIALOG'}),
    ...mapActions({loadProjectLazy: 'project/loadProjectLazy'}),
    ...mapActions('dirs', ['getJobTypesTree']),
    ...mapActions('tasks', ['getTaskEditableFields']),
    ...mapActions('files', ['uploadFile']),
    ...mapActions('documents', ['toAttachment']),
    ...mapActions({
      makeTaskAction: 'tasks/makeTaskAction',
      updateTaskWithError: 'tasks/updateTaskWithError'
    }),

    goToRelatedTask(task) {
      if(task.task_subject.is_deleted) return
      this.$router.push({
        name:'project.task',
        params: {
          taskId: task.task_subject_id
        }
      })
    },
    async initializeItem(item) {
      this.editedItem.name = this.getNested(item, 'name', '');
      this.editedItem.contractor_id = getContractorUser(item)?.id || '',
      this.editedItem.project_name = this.getNested(item, 'project.name', '');
      this.editedItem.initiator_id = this.getNested(item, 'initiator.user.id', '');
      this.editedItem.new_engineer_second_stage_id = this.getNested(item, 'workers').find(x => x.sub_role === TASK_MEMBER_SUB_ROLES.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_SECOND_STAGE)?.user_id
      this.editedItem.worker_id = this.getNested(item, 'workers[0].user_id', '');
      this.editedItem.created_at = parseAt(this.getNested(item, 'created_at', null), { zoned: true })
      this.editedItem.start_at = parseAt(this.getNested(item, 'start_at', null), { zoned: true })
      this.editedItem.axis = this.getNested(item, 'data.axis', '');
      this.editedItem.comment_initiator = this.getNested(item, 'data.comment_initiator', '');
      this.editedItem.ast_contract = this.getNested(item, 'data.ast_contract', null);
      this.editedItem.ast_act_ks2_enabled = this.getNested(item, 'data.ast_act_ks2_enabled', null);
      this.editedItem.ast_acts_ks2_errors_exists = this.getNested(item, 'data.ast_acts_ks2_errors_exists', null);
      this.editedItem.ast_acts_ks2 = this.getNested(item, 'data.ast_acts_ks2', null);

      this.editedItem.objects = item.data.objects
        .map(({ project_object_structure: x }) => x?.house && x?.floor && [x.house, x.floor] )
        .filter(is)
        .map(([house, floor]) => ({ house, floor }))

      this.editedItem.job_types = item.data.job_types.map(({id}) => id);

      this.editedItem.comment_contractor = getCommentContractor(item)
      this.editedItem.comment_worker = getCommentWorker(item)
      this.editedItem.comment_worker_second_stage = getCommentWorkerSecondStage(item)

      this.id = this.getNested(item, 'id', '')
      this.status = this.getNested(item, 'status', '');
      this.expired = this.getNested(item, 'expired', '');

      
      this.task = item
      item.data.project_work_production && (this.documents = [await this.toAttachment({ document: item.data.project_work_production })])

      if (item.data.ast_contract) {
        this.contract = {
          ast_contract_id: getAstContract(item).id,
          ast_work_ids: getAstWorks(item).map(x => x.id),
          ast_work_facts: getAstWorks(item).map(x => ({ ast_work_id: x.id, fact_in_units: null }))
        }
      }

      this.trashedMembers = getTrashedMembers(item) || {}

      this.initialItem = { ...this.editedItem }
    },
    handleChangeDateTime(value) {
      if (value)
        this.editedItem.expired_at = formatForServer(value);
    },

    act(action) {
      const { name, allowed_fields } = action

      const go = data => this.makeTaskAction({
        taskId: this.currentTask.id,
        taskType: this.currentTask.type,
        payload: {
          action: name,
          data
        }
      })
        .catch(fromError)
        .catch(({ messages }) => {
          this.errors = {
            ...messages,
            ...then(messages.action, x => ({ [name]: x }))
          }
          throw { messages }
        })

      const f = allowed_fields.length 
        ? () => this.showDialog({
          dialogName: 'task-acceptance-of-work-dialog',
          payload: { action, task: this.task },
          action: go
        })
        : () => {
          this.$refs['taskTypeAcceptanceOfWork'].validate(on => {
            if (on) {
              this.loading = true;

              go(this.editedItem)
                .then(() => {
                  this.errors = {}
                })
                .finally(() => this.loading = false)
            }
          })
        }

      f()
    },

    async handleUpdate() {
      await this.handleChangeDateTime(this.expired_at);

      this.$refs['taskTypeAcceptanceOfWork'].validate(async on => {
        if (on) {
          this.loading = true;

          const attachment = this.documents[0]

          const file = attachment && attachment.file && await this.uploadFile(attachment.file)
          const document = attachment && attachment.document

          const { 
            ast_contract_id, ast_work_ids, 
            fact_in_units_acceptance_of_work_created, 
            fact_in_units_acceptance_of_work_first_stage_finished, 
            fact_in_units_acceptance_of_work_second_stage_finished 
          } = this.contract

          this.updateTaskWithError({
            taskId: this.currentTask.id,
            taskType: this.currentTask.type,
            payload: {
              ...this.prepareEditedItem({
                contractor_id: { ...this.isEditableField('new_contractor_id') && { key: 'new_contractor_id' } },
                worker_id: { ...this.isEditableField('new_engineer_first_stage_id') && { key: 'new_engineer_first_stage_id' } },
                objects: {
                  to: x => x.map(({ house, floor }) => {
                    let r

                    r ||= floor && { object_type: 'floor', object_id: floor.id }
                    r ||= house && { object_type: 'house', object_id: house.id }

                    return r
                  }).filter(is)
                }
              }),

              ...this.canEditPWP && { file_work_production_id: file?.file_id || null },
              ...this.canEditPWP && { project_document_work_production_id: document?.id || null },

              ...this.canEditContract && { 
                ast_contract_id, ast_work_ids,
                ...fact_in_units_acceptance_of_work_created && {
                  ast_work_facts_acceptance_of_work_created: Object.values(fact_in_units_acceptance_of_work_created)
                },
                ...fact_in_units_acceptance_of_work_first_stage_finished && {
                  ast_work_facts_acceptance_of_work_first_stage_finished: Object.values(fact_in_units_acceptance_of_work_first_stage_finished)
                },
                ...fact_in_units_acceptance_of_work_second_stage_finished && {
                  ast_work_facts_acceptance_of_work_second_stage_finished: Object.values(fact_in_units_acceptance_of_work_second_stage_finished)
                }
              }
            }
          }).then(() => {
            this.errors = {};
          }).catch(({ messages }) => then(messages, x => this.errors = {
            ...x,
            ast_works: Object.entries(x).filter(([k]) => k.startsWith('ast_work_facts')).reduce((r, [k, v]) => ({ ...r, [k]: v }), {})
          })).finally(() => {
            this.loading = false;
          });
        }
      });
    },

    prepareEditedItem(replaceableFields = {}) {
      return Object.entries(this.editedItem).reduce((r, [k, v]) => {
        const replacer = replaceableFields[k] || {}
        const comparator = replacer.comparator || ((a, b) => a === b)
        
        if (!comparator(v, this.initialItem[k])) {
          if (replacer.unwrap && replacer.to)
            return { ...r, ...replacer.to(v)} 

          return {
            ...r,
            [replaceableFields[k]?.key || k]: replaceableFields[k]?.to ? replaceableFields[k]?.to(v) : v
          }
        }
        
        return r
      }, {})
    },

    change(k, v) {
      this.editedItem = {
        ...this.editedItem,
        [k]: v
      }
    }
  }
};
</script>
